import type { ReportRequest, ReportSort } from '@/store/slices/reportingV2/reportingV2.api';
import type { ReportingV2Filters, ReportingV2Grouping } from '@/store/slices/reportingV2/reportingV2Slice';

/**
 * Widget catalog for the canonical Reporting 2.0 page. Each widget is one
 * `/reporting-v2/report` query; the source canonical table is chosen
 * server-side from the requested dimensions/metrics. Base-grain widgets
 * (totals, daypart, channel/device/product) read CANONICAL.PERFORMANCE and
 * populate once the beeswax_performance transform runs; geo/publisher/event
 * read their breakdown tables and have live Beeswax data today.
 */
export interface WidgetSpec {
  key: string;
  title: string;
  dimensions: string[];
  metrics: string[];
  sort?: ReportSort[];
  limit?: number;
  /** Row offset for paged grids. Omitted means the first page. */
  offset?: number;
  /**
   * Forces the request to one or more DSPs regardless of the global Platform
   * filter (review, Lucas on #5133/#4044): a widget that's always one DSP
   * (Google Ads Campaign Breakdown, DV360 Creative/Line Item Performance)
   * must scope its own `limit`/`sort` to that DSP server-side — filtering
   * the fetched rows client-side afterward (e.g. `googleAdsRows`) instead
   * lets every OTHER platform's rows consume the result limit first, which
   * silently truncates a small-volume DSP to a handful of rows with no error.
   */
  dspsOverride?: string[];
}

const desc = (field: string): ReportSort[] => [{ field, dir: 'desc' }];

export const WIDGETS = {
  // Never rendered — exists purely so useReportingPermissions has a reliable,
  // always-fetched `dimensions.dsp` restrictedFields signal for canViewPlatform
  // (ribeye-api#2401). Can't ask `totals` for this: it's dimensions: [], and
  // KpiStrip/KpiCompareTable/ClientRecapCard all hard-assume its single
  // aggregate row stays a single row (`rows[0]`) — grouping it by dsp would
  // silently corrupt all three into showing one platform's numbers as "the
  // totals" (review, flank#4650).
  platformSignal: {
    key: 'platformSignal',
    title: 'Platform Signal (internal)',
    dimensions: ['dsp'],
    metrics: ['impressions'],
    limit: 1,
  },
  totals: {
    key: 'totals',
    title: 'Totals',
    dimensions: [],
    // client_spend/client_cpm (#4446) — permission-gated and Beeswax-only
    // server-side; the request just asks for them like any other metric.
    metrics: [
      'impressions',
      'spend',
      'reach',
      'frequency',
      'clicks',
      'ctr',
      // The Total row's CPC (flank#4018): the backend's cost-bearing denominator,
      // and stripped like spend/cpm for users without wholesale access.
      'cpc',
      'conversions',
      'vcr',
      'client_spend',
      'client_cpm',
      // Social engagement (flank#4634) — Meta/TikTok only; NULL elsewhere, so
      // the Total row reads "—" wherever a platform doesn't report one.
      'link_clicks',
      'post_engagement',
      'video_plays',
      'video_completes',
      'reactions',
      'comments',
      'shares',
    ],
  },
  // NB: no `daypart` entry. It queried the `hour` dimension (the canonical name
  // is `hour_of_day`) and nothing ever read the key — DaypartCard self-fetches
  // under `daypartMetric`. It was one dead query on the critical path of every
  // filter change.
  channel: {
    key: 'channel',
    title: 'Channel',
    dimensions: ['channel'],
    metrics: ['impressions'],
    sort: desc('impressions'),
  },
  device: {
    key: 'device',
    title: 'Device',
    dimensions: ['device'],
    metrics: ['impressions'],
    sort: desc('impressions'),
  },
  // geo / publishers / campaigns / day-of-week / daypart-by-metric are now
  // self-fetched by their own section components (each with its own controls).
} satisfies Record<string, WidgetSpec>;

export const ALL_WIDGETS: WidgetSpec[] = Object.values(WIDGETS);

/**
 * Campaign / Line Item filter keys (#4614). Option values are ribeye id strings;
 * the flat report keys take positive integers. Unified Reporting sends only these
 * for those tiers — never `campaign_ids` / `line_item_ids` alongside.
 */
export const ribeyeFilterKeys = (
  filters: Pick<ReportingV2Filters, 'ribeyeCampaignIds' | 'ribeyeLineItemIds'>,
): Pick<ReportRequest, 'ribeye_campaign_ids' | 'ribeye_line_item_ids'> => ({
  ...(filters.ribeyeCampaignIds.length ? { ribeye_campaign_ids: filters.ribeyeCampaignIds.map(Number) } : {}),
  ...(filters.ribeyeLineItemIds.length ? { ribeye_line_item_ids: filters.ribeyeLineItemIds.map(Number) } : {}),
});

export const buildRequest = (widget: WidgetSpec, filters: ReportingV2Filters): ReportRequest => ({
  dimensions: widget.dimensions,
  metrics: widget.metrics,
  date_range: { start: filters.dateStart, end: filters.dateEnd },
  // A widget-level override always wins: it's the card's own scope, not a
  // user choice, so the global Platform filter narrowing it further (e.g. to
  // a DSP the card isn't) should yield zero rows, not fall back to "all DSPs".
  ...(widget.dspsOverride ? { dsps: widget.dspsOverride } : filters.dsps.length ? { dsps: filters.dsps } : {}),
  ...(filters.whitelabelIds.length ? { whitelabel_ids: filters.whitelabelIds } : {}),
  ...(filters.clientIds.length ? { client_ids: filters.clientIds } : {}),
  ...(filters.advertiserIds.length ? { advertiser_ids: filters.advertiserIds } : {}),
  ...ribeyeFilterKeys(filters),
  ...(filters.productIds.length ? { product_ids: filters.productIds } : {}),
  ...(filters.publishers.length ? { publishers: filters.publishers } : {}),
  ...(filters.channelIds.length ? { channel_ids: filters.channelIds } : {}),
  ...(filters.deviceIds.length ? { device_ids: filters.deviceIds } : {}),
  ...(widget.sort ? { sort: widget.sort } : {}),
  pagination: { limit: widget.limit ?? 1000, offset: widget.offset ?? 0 },
});

export const GROUPING_DIM: Record<Exclude<ReportingV2Grouping, 'all'>, string> = {
  dsp: 'dsp',
  channel: 'channel',
  product: 'product',
};

export const GROUPING_LABEL: Record<ReportingV2Grouping, string> = {
  all: 'All combined',
  dsp: 'Platform',
  channel: 'Channel',
  product: 'Product',
};

/** The grouped-breakdown query for the active grouping (DSP / Channel / Product). */
export const buildGroupedRequest = (
  grouping: Exclude<ReportingV2Grouping, 'all'>,
  filters: ReportingV2Filters,
): ReportRequest =>
  buildRequest(
    {
      key: 'grouped',
      title: 'Grouped',
      dimensions: [GROUPING_DIM[grouping]],
      metrics: ['impressions', 'spend', 'ctr', 'vcr', 'client_spend', 'client_cpm'],
      sort: desc('impressions'),
      limit: 20,
    },
    filters,
  );
